import type { BatteryInput, ChargerInput, ChargeTimeResult } from '@brt/shared-types'
import { CapacityUnit } from '@brt/shared-types'
import { convertCapacity } from '../utils/unitConversion.js'
import { roundTo4 } from '../utils/rounding.js'

export function calculateChargeTime(
  battery: BatteryInput,
  charger: ChargerInput,
): ChargeTimeResult {
  const capacityAh = convertCapacity(battery.capacityValue, battery.capacityUnit, CapacityUnit.AH, battery.voltage)
  const capacityToReplenishAh = capacityAh * (1 - battery.stateOfCharge)

  if (capacityToReplenishAh <= 0) {
    return {
      chargeTimeHours: 0,
      error: null,
      capacityToReplenishAh: 0,
      chargeRateC: 0,
      chargeMethod: 'current',
    }
  }

  const chargeEfficiency = charger.chargeEfficiency ?? 0.90

  if (charger.chargerCurrentValue > 0) {
    const effectiveChargeCurrent = charger.chargerCurrentValue * chargeEfficiency
    const chargeTimeHours = capacityToReplenishAh / effectiveChargeCurrent
    const chargeRateC = charger.chargerCurrentValue / capacityAh

    return {
      chargeTimeHours: Math.round(chargeTimeHours * 100) / 100,
      error: null,
      capacityToReplenishAh: Math.round(capacityToReplenishAh * 100) / 100,
      chargeRateC: Math.round(chargeRateC * 1000) / 1000,
      chargeMethod: 'current',
    }
  }

  if (charger.chargerPower > 0 && battery.voltage > 0) {
    const effectivePower = charger.chargerPower * chargeEfficiency
    const chargeCurrent = effectivePower / battery.voltage
    const chargeTimeHours = capacityToReplenishAh / chargeCurrent
    const chargeRateC = chargeCurrent / capacityAh

    return {
      chargeTimeHours: Math.round(chargeTimeHours * 100) / 100,
      error: null,
      capacityToReplenishAh: Math.round(capacityToReplenishAh * 100) / 100,
      chargeRateC: Math.round(chargeRateC * 1000) / 1000,
      chargeMethod: 'power',
    }
  }

  return {
    chargeTimeHours: null,
    error: 'No valid charger current or power specified',
    capacityToReplenishAh: Math.round(capacityToReplenishAh * 100) / 100,
    chargeRateC: 0,
    chargeMethod: 'current',
  }
}

export interface ChargerCurrentSolution {
  requiredCurrentA: number | null
  requiredPowerW: number | null
  error: string | null
}

export function solveForChargerCurrent(
  targetHours: number,
  battery: BatteryInput,
  chargeEfficiency: number,
): ChargerCurrentSolution {
  if (targetHours <= 0) {
    return { requiredCurrentA: null, requiredPowerW: null, error: 'Target hours must be greater than 0' }
  }
  if (chargeEfficiency <= 0) {
    return { requiredCurrentA: null, requiredPowerW: null, error: 'Charge efficiency must be greater than 0' }
  }

  const capacityAh = convertCapacity(battery.capacityValue, battery.capacityUnit, CapacityUnit.AH, battery.voltage)
  const capacityToReplenishAh = capacityAh * (1 - battery.stateOfCharge)

  if (capacityToReplenishAh <= 0) {
    return { requiredCurrentA: null, requiredPowerW: null, error: 'Battery is already at or above the target state of charge' }
  }

  const requiredCurrentA = roundTo4(capacityToReplenishAh / (targetHours * chargeEfficiency))
  const requiredPowerW = roundTo4(requiredCurrentA * battery.voltage)

  return { requiredCurrentA, requiredPowerW, error: null }
}
